import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { TOKENS } from '../state/colors';
import { fs } from '../state/typography';
import { centroApi } from '../api/centroApi';
import { VersionCentro } from '../types';
import { useToast } from './Toast';
import { VersionCard } from './VersionCard';

const VISIBLES = 3;

interface VersionesSectionProps {
  bolsaId: string;
  codigoCotizacion: string;
  versiones: VersionCentro[];
}

// SPEC-206 (UC-007, design §7b) — "Descargar PDF" baja el archivo exacto que recibió el cliente,
// con el nombre del adjunto.
function guardarArchivo(pdf: Blob, nombre: string): void {
  const url = URL.createObjectURL(pdf);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = nombre;
  enlace.click();
  URL.revokeObjectURL(url);
}

// SPEC-206 (UC-007/UC-008) — versiones enviadas de la más reciente a la más antigua (ya vienen
// ordenadas del backend); hasta 3 tarjetas visibles y el resto tras "Ver N versiones anteriores".
export const VersionesSection: React.FC<VersionesSectionProps> = ({ bolsaId, codigoCotizacion, versiones }) => {
  const { showToast } = useToast();
  const [todas, setTodas] = useState(false);
  if (versiones.length === 0) return null;

  const visibles = todas ? versiones : versiones.slice(0, VISIBLES);
  const ocultas = versiones.length - visibles.length;

  const descargar = async (version: number) => {
    try {
      const pdf = await centroApi.descargarPdfVersion(bolsaId, version);
      guardarArchivo(pdf, `${codigoCotizacion.toLowerCase()}-v${version}.pdf`);
    } catch {
      showToast(`No se pudo descargar el PDF de la v${version}`, 'error');
    }
  };

  return (
    <Box component="section" aria-label="Versiones" data-testid="versiones-section" sx={{ marginTop: '26px' }}>
      <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11), fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', color: TOKENS.muted, marginBottom: '10px' }}>
        VERSIONES · {versiones.length}
      </Typography>
      {visibles.map((version) => {
        const indice = versiones.indexOf(version);
        const anterior = versiones[indice + 1]?.version ?? null;
        return <VersionCard key={version.id} version={version} versionAnterior={anterior} onDescargar={descargar} />;
      })}
      {ocultas > 0 && (
        <Typography
          component="button"
          type="button"
          onClick={() => setTodas(true)}
          sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12), fontWeight: 600, color: TOKENS.purple, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          Ver {ocultas} {ocultas === 1 ? 'versión anterior' : 'versiones anteriores'}
        </Typography>
      )}
    </Box>
  );
};
